import { dasherize } from '../../utils/dom';
import { Product } from '../../utils/product/product-types';
import { ShowcaseGroup, ShowcaseData, HomeShowcase } from './types';

interface ListingInput extends Product {
  categoryName?: string;
}

interface AggregateShowcaseOptions {
  loadedShowcases: HomeShowcase[];
  loadedCategoryShowcases: ShowcaseGroup[];
  showcaseData: ShowcaseData | null | undefined;
  /** Accepted for callers; no row is named after the group any more. */
  categoryGroupLabel?: string;
  currentList: ListingInput[];
  getCategoryById: (id: number) => { label?: string; slug?: string } | undefined;
  visibleShowcaseCount: number;
}

/**
 * Sort showcases so "Other" categories appear at the end
 */
export const sortShowcasesWithOtherAtEnd = (showcases: ShowcaseGroup[]): ShowcaseGroup[] => {
  return [...showcases].sort((a, b) => {
    const aIsOther = a.categoryName.toLowerCase().startsWith('other');
    const bIsOther = b.categoryName.toLowerCase().startsWith('other');

    if (aIsOther && !bIsOther) return 1; // a goes after b
    if (!aIsOther && bIsOther) return -1; // a goes before b
    return 0; // maintain original order
  });
};

/**
 * Group listings by category for client-side showcase generation
 */
export const groupListingsByCategory = (
  listings: ListingInput[],
  getCategoryById: (id: number) => { label?: string; slug?: string } | undefined
): ShowcaseGroup[] => {
  if (!listings || listings.length === 0) return [];

  const groups: Record<number, ShowcaseGroup> = {};

  listings.forEach((listing) => {
    const catId = listing.categoryId || 0;

    // Skip grouping if no valid categoryId
    if (!catId) return;

    const category = getCategoryById(Number(catId));
    const catName = category?.label || listing.categoryName || 'Other';
    const catSlug = category?.slug || dasherize(catName);

    if (!groups[catId]) {
      groups[catId] = {
        categoryId: catId,
        categoryName: catName,
        categorySlug: catSlug,
        listings: [],
      };
    }

    groups[catId].listings.push(listing);
  });

  return Object.values(groups).filter((group) => group.listings.length > 0);
};

/**
 * Transform home showcases to standard ShowcaseGroup format
 */
export const transformHomeShowcases = (
  homeShowcases: HomeShowcase[],
  visibleCount: number
): ShowcaseGroup[] => {
  return homeShowcases.slice(0, visibleCount).map((showcase) => ({
    categoryId: showcase.categoryGroupId,
    categoryName: showcase.categoryGroupName,
    categorySlug: showcase.categoryGroupSlug,
    listings: showcase.listings,
  }));
};

/**
 * Aggregate showcase data from multiple sources with priority ordering
 */
export const aggregateShowcaseData = ({
  loadedShowcases,
  loadedCategoryShowcases,
  showcaseData,
  currentList,
  getCategoryById,
  visibleShowcaseCount,
}: AggregateShowcaseOptions): ShowcaseGroup[] => {
  // Priority 1: Home page category group showcases (lazy loaded)
  if (loadedShowcases && loadedShowcases.length > 0) {
    return transformHomeShowcases(loadedShowcases, visibleShowcaseCount);
  }

  // Priority 2: Browse category page showcases (lazy loaded from showcaseData).
  // There is no "More <group>" row after them any more (removed 2026-10-08):
  // it lumped every thin subcategory into one row with no name a buyer could
  // act on. The tag rows that follow carry those listings instead.
  if (loadedCategoryShowcases && loadedCategoryShowcases.length > 0) {
    return [...loadedCategoryShowcases];
  }

  // Priority 3: Server-side showcase data (fallback for non-lazy pages)
  if (showcaseData?.fullShowcases && showcaseData.fullShowcases.length > 0) {
    return [...showcaseData.fullShowcases];
  }

  // Priority 4: Fall back to client-side grouping
  return groupListingsByCategory(currentList, getCategoryById);
};
